import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import {
  CreateDirectUploadDocument,
  CreateDirectUploadMutation,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'

import { calculateChecksum } from './calculateChecksum'

type DirectUploadsHeader = NonNullable<
  NonNullable<
    CreateDirectUploadMutation['directUploadsMutations']['createDirectuploadsDirectUpload']
  >['directUpload']
>['headers'][number]

export type UploadStatus = 'idle' | 'processing' | 'success' | 'uploading'

export type UploadResult = { type: 'success'; signedId: string } | { type: 'aborted' }

// Internal type for sendToObjectStorage
type StorageUploadResult = { type: 'success' } | { type: 'aborted' }

type UploadAction =
  | { type: 'PROCESSING' }
  | { type: 'RESET' }
  | { type: 'SET_SIGNED_ID'; payload: string }
  | { type: 'SUCCESS' }
  | { type: 'UPDATE_PROGRESS'; payload: number }
  | { type: 'UPLOADING' }

const initialState: UploadState = {
  progress: 0,
  error: null,
  status: 'idle',
  signedId: null,
}

export const useDirectUpload = (config: DirectUploadConfig): UseDirectUploadReturn => {
  const { company } = useCurrentSession()
  const xhrRef = useRef<XMLHttpRequest | null>(null)
  const [state, dispatch] = useReducer(uploadReducer, initialState)

  const [createDirectUpload] = useMutation(CreateDirectUploadDocument, {
    extractorFn: (data: CreateDirectUploadMutation) =>
      data.directUploadsMutations.createDirectuploadsDirectUpload,
  })

  const calculateChecksumWithState = useCallback(calculateChecksum, [])

  const createDirectUploadRequest = useCallback(
    async (file: File, checksum: string): Promise<DirectUploadResult> => {
      const handled = handleResult(
        await createDirectUpload({
          variables: {
            byteSize: file.size,
            checksum,
            contentType: file.type,
            filename: file.name,
            companyId: company.id,
            resourceType: config.resourceType,
            resourceId: config.resourceId,
          },
        }),
        (errors) => {
          const errorMsg = errors?.message || 'Unknown error creating direct upload'
          throw new Error(`Failed to create direct upload: ${errorMsg}`)
        }
      )
      if (!handled.success || !handled.data.directUpload) {
        throw new Error('Failed to create direct upload: No data returned')
      }
      dispatch({
        type: 'SET_SIGNED_ID',
        payload: handled.data.directUpload.signedId,
      })
      return {
        url: handled.data.directUpload.url,
        signedId: handled.data.directUpload.signedId,
        headers: handled.data.directUpload.headers || [],
      }
    },
    [company.id, createDirectUpload, config.resourceId, config.resourceType]
  )

  const sendToObjectStorage = useCallback(
    async (
      url: string,
      file: File,
      contentMD5: string,
      headers: DirectUploadsHeader[]
    ): Promise<StorageUploadResult> => {
      dispatch({ type: 'UPLOADING' })
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhrRef.current = xhr
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            dispatch({
              type: 'UPDATE_PROGRESS',
              payload: event.loaded / event.total,
            })
          }
        })
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            dispatch({ type: 'SUCCESS' })
            resolve({ type: 'success' })
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`))
          }
          xhrRef.current = null
        })
        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed due to network error'))
          xhrRef.current = null
        })
        xhr.addEventListener('abort', () => {
          resolve({ type: 'aborted' })
          xhrRef.current = null
        })
        xhr.open('PUT', url)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.setRequestHeader('Content-MD5', contentMD5)
        const alreadySetHeaders = new Set(['content-type', 'content-md5'])
        headers
          .filter((header) => !alreadySetHeaders.has(header.name.toLowerCase()))
          .forEach((header) => xhr.setRequestHeader(header.name, header.value))
        xhr.send(file)
      })
    },
    []
  )

  const cancelUpload = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort()
    }
  }, [])

  /**
   * Main function that orchestrates the entire direct upload process
   */
  const upload = useCallback(
    async (file: File): Promise<UploadResult> => {
      dispatch({ type: 'PROCESSING' })
      try {
        if (!validateFileType(file)) throw new Error('File type not supported')
        if (!validateFileSize(file)) throw new Error('File is too large')
        const checksum = await calculateChecksumWithState(file)
        const { url, signedId, headers } = await createDirectUploadRequest(file, checksum)
        const storageUploadStatus = await sendToObjectStorage(url, file, checksum, headers)

        if (storageUploadStatus.type === 'aborted') {
          return { type: 'aborted' }
        }
        // If sendToObjectStorage resolved with { type: 'success' } and did not throw, it's a success.
        return { type: 'success', signedId }
      } catch (error) {
        // Rethrow other errors to be handled by the caller
        // This includes validation errors, createDirectUploadRequest errors, and actual XHR errors from sendToObjectStorage
        throw error
      }
    },
    [calculateChecksumWithState, createDirectUploadRequest, sendToObjectStorage]
  )

  useEffect(() => {
    return () => {
      if (xhrRef.current) {
        xhrRef.current.abort()
      }
    }
  }, [])

  return useMemo(
    () => ({
      upload,
      progress: state.progress,
      status: state.status,
      cancelUpload,
    }),
    [state.progress, state.status, upload, cancelUpload]
  )
}

interface DirectUploadResult {
  signedId: string
  url: string
  headers: DirectUploadsHeader[]
}

interface DirectUploadConfig {
  resourceId?: string
  resourceType: string
}

interface UploadState {
  error: Error | null
  progress: number
  signedId: string | null
  status: UploadStatus
}

interface UseDirectUploadReturn {
  upload: (file: File) => Promise<UploadResult>
  progress: number
  status: UploadStatus
  cancelUpload: () => void
}

function uploadReducer(state: UploadState, action: UploadAction): UploadState {
  switch (action.type) {
    case 'RESET':
      return initialState
    case 'PROCESSING':
      return { ...state, status: 'processing', error: null, progress: 0 }
    case 'UPLOADING':
      return { ...state, status: 'uploading', progress: 0, error: null }
    case 'UPDATE_PROGRESS':
      return { ...state, progress: action.payload }
    case 'SET_SIGNED_ID':
      return { ...state, signedId: action.payload }
    case 'SUCCESS':
      return { ...state, status: 'success', progress: 1, error: null }
    default:
      return state
  }
}

function validateFileType(file: File) {
  return Boolean(acceptedContentTypes()[file.type])
}

function validateFileSize(file: File) {
  return file.size <= 2 * 1024 * 1024 * 1024 // 2GB
}
